// src/public/tests/SocialContextTest.js
import { useState, useEffect } from 'react';
import {
    Paper, Box, Typography, Button, LinearProgress, Stack, Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para imagen draggable
const SortableImage = ({ id, imageUrl, index }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                width: '200px',
                height: '200px',
                border: '3px solid #1976d2',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                backgroundColor: '#fff',
                boxShadow: isDragging ? 6 : 2,
                '&:hover': {
                    boxShadow: 4,
                },
            }}
        >
            <img
                src={imageUrl}
                alt={`Imagen ${id}`}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                }}
            >
                {index + 1}
            </Box>
        </Box>
    );
};

// Componente principal del test
const SocialContextTest = ({
    responses,
    onAnswerChange,
    currentQuestion,
    onQuestionChange,
    onComplete,
    saving,
    language = 'es',
    timeRemaining = null
}) => {
    const { t } = useTranslation();

    const totalQuestions = 20;
    const progress = ((currentQuestion + 1) / totalQuestions) * 100;
    const questionNumber = currentQuestion + 1;

    const [imageOrder, setImageOrder] = useState([]);
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    useEffect(() => {
        const savedOrder = responses[questionNumber];
        if (savedOrder && Array.isArray(savedOrder) && savedOrder.length === 4) {
            setImageOrder(savedOrder);
        } else {
            const letters = ['a', 'b', 'c', 'd'];
            const shuffled = [...letters].sort(() => Math.random() - 0.5);
            setImageOrder(shuffled);
            // Guardar el orden inicial aleatorio como respuesta
            onAnswerChange(questionNumber, shuffled);
        }
    }, [currentQuestion, questionNumber, responses]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (active.id !== over?.id) {
            setImageOrder((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newOrder = arrayMove(items, oldIndex, newIndex);
                onAnswerChange(questionNumber, newOrder);
                return newOrder;
            });
        }
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleNext = async () => {
        if (currentQuestion < totalQuestions - 1) {
            await onQuestionChange(currentQuestion + 1, responses);
        } else {
            await onComplete(responses);
        }
    };

    const handleSkip = async () => {
        const updatedResponses = { ...responses };
        delete updatedResponses[questionNumber];
        if (currentQuestion < totalQuestions - 1) {
            await onQuestionChange(currentQuestion + 1, updatedResponses);
        } else {
            await onComplete(updatedResponses);
        }
    };

    // Cargar imágenes dinámicamente
    const imagesCtx = require.context('../assets/social-context', false, /\.(jpg|jpeg|png)$/);
    const imageIndex = imagesCtx.keys().reduce((acc, key) => {
        const base = key.replace('./', '').replace(/\.(jpg|jpeg|png)$/i, '');
        acc[base] = imagesCtx(key);
        return acc;
    }, {});

    const getImageUrl = (letter) => {
        const base = `${questionNumber}${letter}`;
        return imageIndex[base] || '';
    };

    return (
        <Paper elevation={3} sx={{ p: 4 }}>
            {/* Progreso */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('reasoningTests.questionNumber', { number: questionNumber })} {t('of')} {totalQuestions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {Math.round(progress)}%
                    </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
            </Box>

            {/* Instrucciones */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('reasoningTests.orderImagesChronologically')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('reasoningTests.dragToOrderInstructions')}
                </Typography>
            </Box>

            {/* Área de drag & drop */}
            <Box sx={{ mb: 4 }}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={imageOrder}
                        strategy={horizontalListSortingStrategy}
                    >
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                gap: 2,
                                overflowX: 'auto',
                                width: '100%',
                            }}
                        >
                            {imageOrder.map((letter, index) => (
                                <SortableImage
                                    key={letter}
                                    id={letter}
                                    imageUrl={getImageUrl(letter)}
                                    index={index}
                                />
                            ))}
                        </Stack>
                    </SortableContext>

                    <DragOverlay>
                        {activeId ? (
                            <Box
                                sx={{
                                    width: '200px',
                                    height: '200px',
                                    border: '3px solid #1976d2',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    opacity: 0.8,
                                    boxShadow: 6,
                                }}
                            >
                                <img
                                    src={getImageUrl(activeId)}
                                    alt={`Imagen ${activeId}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </Box>

            {/* Indicador de posiciones */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Stack direction="row" spacing={2} justifyContent="center">
                    {['1º', '2º', '3º', '4º'].map((pos) => (
                        <Box
                            key={pos}
                            sx={{
                                width: '200px',
                                textAlign: 'center',
                                py: 1,
                                backgroundColor: 'grey.100',
                                borderRadius: 1,
                                fontWeight: 'bold',
                                color: 'text.secondary',
                            }}
                        >
                            {pos}
                        </Box>
                    ))}
                </Stack>
            </Box>

            {/* Alerta de tiempo */}
            {timeRemaining !== null && timeRemaining < 60 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('reasoningTests.lessThanOneMinute')}
                </Alert>
            )}

            {/* Botones de navegación */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleSkip}
                    disabled={saving}
                >
                    {t('reasoningTests.skipQuestion')}
                </Button>

                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={saving || imageOrder.length !== 4}
                >
                    {saving
                        ? t('saving')
                        : currentQuestion === totalQuestions - 1
                            ? t('reasoningTests.finish')
                            : t('reasoningTests.next')
                    }
                </Button>
            </Box>
        </Paper>
    );
};

export default SocialContextTest;